import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";

import { Text } from "@/atoms";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useConfetti, useCountUp } from "@/hooks";
import {
  acknowledgeGiftCardTransferById,
  getNotAcknowledgedGiftCards,
  GiftCardPreviewImage,
  refreshNotAcknowledgedGiftCardsQuery,
} from "@/modules/giftCards";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import GiftboxV3 from "atoms/icon/icons/giftbox_v3.svg";
import RepeatCashIcon from "atoms/icon/icons/RepeatCash.svg";

import { V3ShareFriendsPage_URL } from "./V3ShareFriendsPage";

type AnimationStep = "card" | "claimed" | "converted" | "complete";

const V3GiftCardRedeemPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<AnimationStep>("card");
  const { triggerConfetti } = useConfetti();

  const { data: notAcknowledgedGiftCards = [] } = useQuery({
    queryKey: getNotAcknowledgedGiftCards.keyed().queryKey,
    queryFn: () => getNotAcknowledgedGiftCards(),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async (transferIds: string[]) => {
      await Promise.all(
        transferIds.map((id) =>
          acknowledgeGiftCardTransferById({ transferId: id })
        )
      );
    },
    onSuccess: () => {
      refreshNotAcknowledgedGiftCardsQuery();
    },
    onError: (error: Error) => {
      console.log("error", error);
    },
  });

  useEffect(() => {
    // Step 1: Show card for 1.5s
    const timer1 = setTimeout(() => {
      setStep("claimed");
    }, 1500);

    // Step 2: Show "claimed" message for 2s
    const timer2 = setTimeout(() => {
      setStep("converted");
      triggerConfetti();
    }, 3500);

    // Step 3: Show "converted" for 2s
    const timer3 = setTimeout(() => {
      setStep("complete");
    }, 5500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const handleContinue = () => {
    acknowledgeMutation.mutate(notAcknowledgedGiftCards.map((gc) => gc.id));

    navigate(V3ShareFriendsPage_URL);
  };

  const totalGiftCardAmount = notAcknowledgedGiftCards.reduce(
    (acc, gc) => acc + gc.giftCardItemAmountInCents / 100,
    0
  );

  const giftCardCountSum = useCountUp({
    to: totalGiftCardAmount,
    from: 0,
    duration: 1500,
    delay: 250,
  });

  const getGiftCardClaimText = () => {
    if (notAcknowledgedGiftCards.length === 1) {
      return "Your gift card has been claimed.";
    }
    return "Your gift cards have been claimed.";
  };

  const renderGiftCardsToClaim = () => {
    if (notAcknowledgedGiftCards.length === 1) {
      const firstGiftCard = notAcknowledgedGiftCards[0];
      return (
        <div>
          <div className="h-[200px] w-[300px] overflow-hidden rounded-xl shadow-md">
            <GiftCardPreviewImage
              backgroundImage={firstGiftCard.imageUrl as string}
              amount={(
                firstGiftCard.giftCardItemAmountInCents / 100
              ).toString()}
              size="large"
            />
          </div>
          <Text
            text={`From: ${firstGiftCard.senderInformation}`}
            variant="body1"
            className="pt-3"
            component="p"
          />
          <Text
            text={firstGiftCard.personalMessage}
            variant="body1"
            className="pt-3"
          />
        </div>
      );
    }

    return (
      <div className="-mx-8 overflow-hidden">
        <Swiper
          className="!px-8"
          slidesPerView="auto"
          spaceBetween={24} // 24px = gap-6
          freeMode={true}
          grabCursor={true}
        >
          {notAcknowledgedGiftCards.map((listing) => {
            return (
              <SwiperSlide
                key={listing.id}
                className="!w-auto cursor-pointer pb-4"
              >
                <div className="h-[200px] w-[300px] overflow-hidden rounded-xl shadow-md">
                  <GiftCardPreviewImage
                    backgroundImage={listing.imageUrl as string}
                    amount={(
                      listing.giftCardItemAmountInCents / 100
                    ).toString()}
                    size="large"
                  />
                </div>
                <Text
                  text={`From: ${listing.senderInformation}`}
                  variant="body1"
                  className="pt-3"
                  component="p"
                />
                <Text
                  text={listing.personalMessage}
                  variant="body1"
                  className="pt-3"
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );
  };

  return (
    <MobileViewportContainer>
      <div className="flex min-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-full max-w-sm flex-col items-center justify-center gap-9 px-6">
        <div className="flex w-full flex-col items-center gap-6">
          {/* Icon - appears in step 2+ */}
          <AnimatePresence>
            {step !== "card" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Icon svg={GiftboxV3} size="xlarge" color="brandColor" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Text - appears in step 2+ */}
          <AnimatePresence>
            {step !== "card" && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="text-center font-serif text-[30px] font-normal italic leading-[36px] text-[#091f24]"
              >
                {getGiftCardClaimText()}
              </motion.p>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: ANIMATION_DURATION }}
            className="mx-auto h-48 w-full overflow-hidden rounded-lg shadow-lg"
          >
            {renderGiftCardsToClaim()}
          </motion.div>

          {/* Converted badge - appears in step 3+ */}
          <AnimatePresence>
            {(step === "converted" || step === "complete") && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-center gap-2"
              >
                <p
                  className="font-sans text-sm font-normal leading-[18px]"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  Converted to RepeatCash
                </p>
                <div
                  className="relative flex min-h-[60px] items-center
               overflow-hidden rounded-full bg-white/10 py-1 pl-3 pr-5
               shadow-[0_3px_16px_rgba(0,0,0,0.06)] backdrop-blur-xl"
                >
                  <div
                    className="pointer-events-none absolute inset-0 rounded-[inherit]
               shadow-[inset_1px_1px_0_rgba(255,255,255,0.6),inset_-1px_-1px_0_rgba(255,255,255,0.6)] ring-1
               ring-white/40"
                  ></div>
                  <div className="relative z-10 flex items-center">
                    <RepeatCashIcon className="mr-2 h-10 w-10" />
                    <span className="relative bottom-1 font-medium ">$</span>
                    <span className="font-serif text-h2 font-medium text-[#1B1B1B]">
                      {giftCardCountSum}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Continue Button - only show when animation complete */}
        {step === "complete" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="w-full"
          >
            <Button fullWidth onClick={handleContinue}>
              Continue
            </Button>
          </motion.div>
        )}
      </div>
    </MobileViewportContainer>
  );
};

export default V3GiftCardRedeemPage;
